// redux/slice/mapSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchMapTypes = createAsyncThunk(
  "maps/fetchMapTypes",
  async (params = { is_active: true }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const searchParams = new URLSearchParams();
      if (params.is_active !== undefined) {
        searchParams.append("is_active", params.is_active);
      }
      if (params.is_map_georeference !== undefined) {
        searchParams.append("is_map_georeference", params.is_map_georeference);
      }
      const res = await Axios.get(
        `/maps/map-types/all?${searchParams.toString()}`,
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchMapLocations = createAsyncThunk(
  "maps/fetchMapLocations",
  async (
    {
      map_type_id,
      parent_id = null,
      page = 1,
      limit = 10,
      direction = "forward",
      search = "",
      mode = "navigate",
      show_deleted = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const auth = authHeaders();
      const res = await Axios.get("/maps/map-locations", {
        params: {
          map_type_id,
          parent_id, // 👈 THIS IS THE KEY
          // Only sent when on, so the default request is unchanged.
          ...(show_deleted ? { show_deleted: true } : {}),
          page,
          limit,
          search,
        },
        ...auth,
      });

      return {
        parent_id,
        response: res.data,
        mode,
        direction,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createMapLocation = createAsyncThunk(
  "maps/createMapLocation",
  async ({ map_type_id, parent_id, name, latitude = null, longitude = null }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.post(
        "/maps/map-locations",
        { map_type_id, parent_id, name, latitude, longitude },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMapLocation = createAsyncThunk(
  "maps/updateMapLocation",
  async ({ id, name, latitude, longitude }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        `/maps/map-locations/${id}`,
        // The pin only when given: other callers that rename don't clear it.
        latitude === undefined && longitude === undefined ? { name } : { name, latitude, longitude },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMapStatus = createAsyncThunk(
  "maps/updateMapStatus",
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        `/maps/map-locations/${id}`,
        { is_active },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const reorderMapLocations = createAsyncThunk(
  "maps/reorderMapLocations",
  async ({ id, new_sequence }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        "/maps/map-locations/reorder",
        { id, new_sequence },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


/**
 * Bulk add map files to the location currently open.
 *
 * One request carrying every file, because the create-or-replace decision is the
 * server's to make: it is the only place that can check a title against existing
 * rows and their georeference without racing another operator.
 *
 * Resolves to a report — created / replaced / failed — rather than a single
 * outcome, so rows fail independently and the caller can show which ones need
 * attention. A rejected value here means the whole request was refused (a limit,
 * or a bad location), not that individual files failed.
 */
export const bulkCreateMapFiles = createAsyncThunk(
  "maps/bulkCreateMapFiles",
  async ({ map_type_id, location_id, files, titles }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();

      const formData = new FormData();
      formData.append("map_type_id", map_type_id);
      if (location_id) formData.append("location_id", location_id);

      // Sent as JSON rather than repeated fields, so a title containing a comma
      // survives and index alignment with `files` is explicit.
      formData.append("titles", JSON.stringify(titles));

      files.forEach((file) => formData.append("files", file));

      const res = await Axios.post("/maps/map-files/bulk", formData, {
        ...auth,
        headers: {
          ...auth.headers,
          "Content-Type": "multipart/form-data",
        },
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Soft delete a map file.
 *
 * Nothing leaves object storage and the georeference is untouched, so this is
 * reversible — which is what the confirmation tells the operator.
 */
export const deleteMapFile = createAsyncThunk(
  "maps/deleteMapFile",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await Axios.delete(`/maps/map-files/${id}`, authHeaders());
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Undo a soft delete.
 *
 * Refused when the title has been taken since — uniqueness ignores deleted
 * rows, so the name can legitimately be in use by the time someone restores.
 */
export const restoreMapFile = createAsyncThunk(
  "maps/restoreMapFile",
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/maps/map-files/${id}/restore`, {}, authHeaders());
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createMapFile = createAsyncThunk(
  "maps/createMapFile",
  async ({ map_type_id, location_id, file, title }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();

      const formData = new FormData();
      formData.append("map_type_id", map_type_id);
      formData.append("location_id", location_id);
      formData.append("file", file);
      if (title) formData.append("title", title);

      const res = await Axios.post(
        "/maps/map-files",
        formData,
        {
          ...auth,
          headers: {
            ...auth.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMapFile = createAsyncThunk(
  "maps/updateMapFile",
  async ({ id, file, title }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();

      const formData = new FormData();
      if (file) formData.append("file", file);
      if (title) formData.append("title", title);

      const res = await Axios.patch(
        `/maps/map-files/${id}`,
        formData,
        {
          ...auth,
          headers: {
            ...auth.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const reorderMapFiles = createAsyncThunk(
  "maps/reorderMapFiles",
  async ({ id, new_sequence }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        "/maps/map-files/reorder",
        { id, new_sequence },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const georeferenceMapFile = createAsyncThunk(
  "maps/georeferenceMapFile",
  async ({ id, gcpPoints, corners, alignmentMode, file, crop }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const formData = new FormData();
      if (gcpPoints && gcpPoints.length > 0) {
        formData.append("gcpPoints", JSON.stringify(gcpPoints));
      }
      if (corners && corners.length > 0) {
        formData.append("corners", JSON.stringify(corners));
      }
      if (alignmentMode) {
        formData.append("alignmentMode", alignmentMode);
      }
      if (file) {
        formData.append("file", file);
      }
      // How the sheet was cropped, instead of the cropped pixels; the backend
      // rebuilds the crop from the original at full resolution.
      if (crop) {
        formData.append("crop", JSON.stringify(crop));
      }

      const res = await Axios.post(
        `/maps/map-files/${id}/georeference`,
        formData,
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchGeoreferenceList = createAsyncThunk(
  "maps/fetchGeoreferenceList",
  async ({ page = 1, limit = 10, search = "", map_type_id = "", is_georeferenced = "", location_ids = "", to_check = "" }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.get("/maps/map-files/georeference/list", {
        params: { page, limit, search, map_type_id, is_georeferenced, location_ids, to_check },
        ...auth
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


const mapSlice = createSlice({
  name: "maps",
  initialState: {
    list: [],
    mapList: {
      data: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 0,
      },
      next_level_label: null,
      type: null,
    },
    georefList: {
      data: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 0,
      }
    },
    navStack: [],
    isLoading: false,
    error: null,
  },
  reducers: {
    popNavStack(state) {
      state.navStack.pop();
    },
    truncateNavStack(state, action) {
      const index = action.payload;
      state.navStack = state.navStack.slice(0, index + 1);
    },
    resetMapNavigation(state) {
      state.navStack = [];
      state.mapList = {
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          total_pages: 0,
        },
        next_level_label: null,
        type: null,
        count: 0,
        parent_id: null,
      };
    },
    setNavStack(state, action) {
      state.navStack = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMapTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchMapTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload?.data || [];
      })
      .addCase(fetchMapTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchMapLocations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMapLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        const { parent_id, direction, mode } = action.payload;
        if (
          mode === "navigate" &&
          direction === "forward" &&
          parent_id &&
          state.mapList.type === "dropdown"
        ) {
          state.navStack.push({
            mapList: state.mapList,
            parent_id,
          });
        }
        state.mapList = {
          data: action.payload?.response?.data?.data || [],
          pagination: action.payload?.response?.data?.pagination || {},
          next_level_label: action.payload?.response?.data?.next_level_label || null,
          count: action.payload?.response?.data?.total_root_locations,
          type: action.payload?.response?.data?.type || null,
          parent_id,
        };
      })
      .addCase(fetchMapLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchGeoreferenceList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGeoreferenceList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.georefList = {
          data: action.payload?.data?.data || [],
          pagination: action.payload?.data?.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            total_pages: 0,
          },
          // Imported sheets waiting for review, under the same filters.
          to_check_total: action.payload?.data?.to_check_total || 0,
        };
      })
      .addCase(fetchGeoreferenceList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});
export const { popNavStack, resetMapNavigation, truncateNavStack, setNavStack } = mapSlice.actions;
export default mapSlice.reducer;
